import { shareIdentity } from './householdShare';
import type { Planning, UsageUnit } from './purchasePlanning';
export type TaxSourceReview = { documentId: string; locator: string; reviewed: boolean };
export type RentalTax = {
  operatingCostsMonthly: number | null;
  marketWarmRentMonthly: number | null;
  marketGrowthPercent: number;
  permanentResidential: boolean;
  comparison: TaxSourceReview;
  prognosis: TaxSourceReview & { result: 'unknown' | 'positive' | 'negative' };
};
export const newRentalTax = (): RentalTax => ({
  operatingCostsMonthly: null, marketWarmRentMonthly: null, marketGrowthPercent: 2,
  permanentResidential: false, comparison: { documentId: '', locator: '', reviewed: false },
  prognosis: { result: 'unknown', documentId: '', locator: '', reviewed: false },
});
const money = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1000000;
const sourceReviewed = (source: TaxSourceReview | undefined) => Boolean(source?.reviewed && source.documentId && source.locator.trim());
/** Avoid displaying a rounded legal threshold on its lower side. */
export function rentalTaxPercent(ratio: number) {
  for (const threshold of [50, 66]) {
    if (ratio * 100 < threshold && Math.round(ratio * 10000) >= threshold * 100) return `knapp unter ${threshold} %`;
  }
  return `${(ratio * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}
export const rentalTaxLabels = {
  comparison_missing: 'Warmmietvergleich noch offen', prognosis_missing: 'Totalüberschussprognose noch offen',
  full: 'Voller Werbungskostenanteil als Planungsannahme', partial: 'Anteiliger Werbungskostenabzug',
};
/** §21(2): compare agreed warm amounts in cents; vacancy is an income risk,
 * not a discount of the agreed contract rent. Future market/operating growth is
 * an explicit scenario assumption. This does not create a surplus prognosis. */
export function rentalTaxMonth(unit: UsageUnit, month: number) {
  const tax = unit.rentalTax;
  const years = Math.max(0, month - unit.rentalStartMonth) / 12;
  const growthValid = tax && Number.isFinite(tax.marketGrowthPercent) && tax.marketGrowthPercent >= 0 && tax.marketGrowthPercent <= 50;
  const growth = growthValid ? (1 + tax.marketGrowthPercent / 100) ** years : 1;
  const operating = money(tax?.operatingCostsMonthly) && growthValid ? tax.operatingCostsMonthly * growth : null;
  const cold = unit.rentMonthly * (1 + unit.rentGrowthPercent / 100) ** years;
  const market = money(tax?.marketWarmRentMonthly) && tax.marketWarmRentMonthly > 0 && growthValid ? tax.marketWarmRentMonthly * growth : null;
  const paidCents = operating === null ? null : Math.round(cold * 100) + Math.round(operating * 100);
  const marketCents = market === null ? null : Math.round(market * 100);
  const comparisonValid = tax?.permanentResidential && sourceReviewed(tax.comparison)
    && paidCents !== null && Number.isSafeInteger(paidCents) && paidCents >= 0
    && marketCents !== null && Number.isSafeInteger(marketCents) && marketCents > 0;
  let state: keyof typeof rentalTaxLabels = 'comparison_missing';
  let factor: number | null = null;
  const ratio = comparisonValid ? paidCents! / marketCents! : null;
  if (ratio !== null) {
    if (BigInt(paidCents!) * 100n >= BigInt(marketCents!) * 66n) { factor = 1; state = 'full'; }
    else if (BigInt(paidCents!) * 100n < BigInt(marketCents!) * 50n) { factor = ratio; state = 'partial'; }
    else if (sourceReviewed(tax!.prognosis) && tax!.prognosis.result !== 'unknown') {
      factor = tax!.prognosis.result === 'positive' ? 1 : ratio;
      state = factor === 1 ? 'full' : 'partial';
    } else state = 'prognosis_missing';
  }
  return { state, factor, ratio, operating, cold, market };
}
const comparisonInputs = (u: UsageUnit) => [u.use,u.areaM2,u.rentMonthly,u.rentalStartMonth,u.rentGrowthPercent,u.legalRentabilityConfirmed,u.documentId,
  u.rentalTax?.operatingCostsMonthly,u.rentalTax?.marketWarmRentMonthly,u.rentalTax?.marketGrowthPercent,u.rentalTax?.permanentResidential,
  u.rentalTax?.comparison.documentId,u.rentalTax?.comparison.locator];
const prognosisSource = (u: UsageUnit) => [u.rentalTax?.prognosis.result,u.rentalTax?.prognosis.documentId,u.rentalTax?.prognosis.locator];
// Income tax settings and the displayed horizon do not form a pre-tax, external
// total-surplus review. Review flags themselves never invalidate a new review.
const economicContext = (p: Planning | undefined) => p && [p.costs,p.requests,shareIdentity(p.finance),
  [p.finance.purchasePrice,p.finance.purchaseFeesPercent,p.finance.equity,p.finance.retainedReserve,p.finance.selectedRequestId,p.finance.selectedRequestIds,p.finance.financingMode,p.finance.assumedInterestRate,p.finance.assumedRepaymentRate,p.finance.buildingValueSharePercent,p.finance.completionYear,p.finance.interestAllocation,p.finance.rentalInterestSharePercent,p.finance.allocationDocumentId,p.finance.feeMode],
  p.units.map(u => [...comparisonInputs(u),u.vacancyPercent,u.ownerCostsMonthly,u.maintenanceReserveMonthly,u.actualDeductibleCostsMonthly])];
export function invalidateRentalTaxReviews(previous: Planning | undefined, next: Planning): Planning {
  const changed = JSON.stringify(economicContext(previous)) !== JSON.stringify(economicContext(next));
  return { ...next, units: next.units.map(unit => {
    if (!unit.rentalTax) return unit;
    const old = previous?.units.find(u => u.id === unit.id);
    const comparisonChanged = !old || JSON.stringify(comparisonInputs(old)) !== JSON.stringify(comparisonInputs(unit));
    const prognosisChanged = !old || JSON.stringify(prognosisSource(old)) !== JSON.stringify(prognosisSource(unit));
    return { ...unit, rentalTax: { ...unit.rentalTax,
      comparison: { ...unit.rentalTax.comparison, reviewed: comparisonChanged ? false : unit.rentalTax.comparison.reviewed },
      prognosis: { ...unit.rentalTax.prognosis, reviewed: changed || prognosisChanged ? false : unit.rentalTax.prognosis.reviewed },
    } };
  }) };
}
